import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import cn from 'classnames';
import { useBiAction } from '../BiTracking';
import { useIsInBottomSheet } from '../BottomSheet';
import { useIsInCallout } from '../Callout';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { useDevice } from '../Device';
import { useIntentBehavior } from '../IntentBehavior';
import { Spinner } from '../Spinner';
import { Tooltip } from '../Tooltip';
import classes from './Button.module.scss';
import { spinnerSize } from './Button.utils';
const calloutSizeMap = {
    large: 'medium',
    medium: 'small',
    small: 'xsmall',
    xsmall: 'xsmall',
    iconOnly: 'iconOnly'
};
const downsizeForCallout = (size)=>calloutSizeMap[size];
export function Button(props) {
    var _props_roundness;
    const { className, type, size = 'medium', skin = 'primary', prefixIcon, suffixIcon, as, ref, children, fluid, intent, disabled, loading, loadingText, tooltip, noWrap, // SplitButton introduces these props, which causes React console errors about unknown props for button element, so we ignore them here.
    disableElevation: _disableElevation, disableFocusRipple: _disableFocusRipple, disableRipple: _disableRipple, fullWidth: _fullWidth, bi, onClick, testId, disabledSkin } = props, rest = _object_without_properties(props, [
        "className",
        "type",
        "size",
        "skin",
        "prefixIcon",
        "suffixIcon",
        "as",
        "ref",
        "children",
        "fluid",
        "intent",
        "disabled",
        "loading",
        "loadingText",
        "tooltip",
        "noWrap",
        "disableElevation",
        "disableFocusRipple",
        "disableRipple",
        "fullWidth",
        "bi",
        "onClick",
        "testId",
        "disabledSkin"
    ]);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const isInCallout = useIsInCallout();
    const effectiveSize = isInCallout && isMobileDevice ? downsizeForCallout(size) : size;
    const roundness = (_props_roundness = props.roundness) !== null && _props_roundness !== void 0 ? _props_roundness : isMobileDevice || isInBottomSheet ? 'm' : 's';
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const isDisabled = disabledFromIntent || disabled || loading;
    const isFocusedDisabled = isDisabled && disabledSkin === 'focused';
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const Component = as || 'button';
    const isOutline = skin === 'dangerOutline' || skin === 'primaryOutline';
    const logBiAction = useBiAction();
    const handleClick = useCallback((event)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'button',
                entityName: bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        return onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [
        bi,
        logBiAction,
        onClick,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, tooltip || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(classes.wrapper, {
                        [classes.fluid]: fluid
                    }, className),
                    "data-testid": testId && "".concat(testId, "-wrapper"),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsxs(Component, _object_spread_props(_object_spread({
            ref: ref,
            type: type || 'button',
            disabled: isDisabled,
            className: cn(classes.root, classes["".concat(effectiveSize, "Size")], classes["".concat(skin, "Skin")], classes["".concat(roundness, "Roundness")], {
                [classes.fluid]: fluid,
                [classes.noWrap]: noWrap,
                [classes.focusedDisabled]: isFocusedDisabled
            }, !hasTooltip && className),
            onClick: handleClick
        }, rest, testId != null ? {
            'data-testid': testId
        } : {}), {
            children: [
                loading ? /*#__PURE__*/ _jsx("div", {
                    className: classes.prefixIcon,
                    "data-testid": testId && "".concat(testId, "-loading"),
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        size: spinnerSize[effectiveSize],
                        intent: isOutline ? 'default' : 'primary'
                    })
                }) : prefixIcon && /*#__PURE__*/ _jsx("div", {
                    className: classes.prefixIcon,
                    children: prefixIcon
                }),
                loading ? loadingText : children,
                suffixIcon ? /*#__PURE__*/ _jsx("div", {
                    className: classes.suffixIcon,
                    children: suffixIcon
                }) : null
            ]
        }))
    });
}
